import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe, listTasks, logChase, resolveWaitingOn, setWaitingOn, type Task } from '../api';
import { useProjectRole } from '../roles';
import { readableDay, readableInstant } from '../dateLabels';
import { FailureNote } from './FailureNote';

/**
 * A promise held by somebody who will never log in, on the two screens where it is read.
 *
 * The deliverable leaves planning — `pickCandidates` drops it, so it is not in the shortlist and
 * the planner will not put it on the calendar — and the *chase* stays on Today, because chasing
 * is work you can actually do. The rail is where that split is visible: it is the one place a
 * wait can be acted on, and it sits above the day rather than inside a pane for the same reason
 * `CarriedOver` does.
 *
 * **Nothing here computes anything.** `waitingDays` arrives on the task, counted by the server in
 * the account's zone from the last chase (or from the start of the wait if nobody has been
 * chased), and the order arrives from `listWaitingOn`. A rail that subtracted `waitingSince` from
 * `Date.now()` would answer in the browser's zone and by elapsed hours — one day short for most
 * of the day — and a rail that re-sorted would be the second definition of "worst promise". Both
 * are the INV-UTC failure this feature was written around; see `services/waitingOn.ts`.
 *
 * **"Waiting on" now means only this.** It used to mean three things: `BlockedBanner` said
 * "Waiting on <task title>" about an open *blocker*, Today's empty shortlist said "Nothing
 * waiting" about having no candidates, and GoalsToday said "Waiting on the shortlist" about work
 * ready to be dragged onto the day. The other two were renamed in Phase 72 — "Blocked by …" and
 * "On the shortlist:" — leaving the word to the one fact whose remedy is chasing a person.
 * Everything below still names the party in the same breath as the word, which is what makes the
 * sentence readable on its own rather than only by elimination.
 */

/**
 * How long they have been silent, said the same way on every surface.
 *
 * Zero is two different situations and they get two sentences: a wait you opened today has never
 * been silent for a day, and a chase you logged an hour ago is why the number went back to zero.
 * "0 days" would be true of both and informative about neither — and on the rail, where the whole
 * point of pressing **Chased** is to see that it registered, "0 days" beside a party's name reads
 * as a rendering fault.
 */
function silenceText(task: Pick<Task, 'waitingDays' | 'lastChasedAt'>): string {
  const days = task.waitingDays ?? 0;
  if (days === 0) return task.lastChasedAt ? 'chased today' : 'since today';
  return `silent ${days} ${days === 1 ? 'day' : 'days'}`;
}

/**
 * How often they have been chased, and when last — the sentence the count was filed for.
 *
 * "Chased twice, last on 1 Sep" rather than "last chased 1 Sep", because the two say different
 * things about the same promise: one poke that went unanswered is a wait, and four are a problem
 * with the counterparty. The count is `chaseCount` off the payload and is the chases of *this*
 * wait — re-setting a wait starts a new one and the tally with it — so it always agrees with the
 * "Chased X, day N" lines in the thread below this banner for the promise being read.
 *
 * The first chase says "once, on 1 Sep" rather than "1 time": at one, the date is the news and
 * the count barely is.
 */
function chaseText(task: Pick<Task, 'chaseCount' | 'lastChasedAt'>, timezone: string): string {
  if (!task.lastChasedAt) return 'not chased yet';
  const day = dayInZone(task.lastChasedAt, timezone);
  const times = task.chaseCount ?? 1;
  if (times <= 1) return `chased once, on ${day}`;
  if (times === 2) return `chased twice, last on ${day}`;
  return `chased ${times} times, last on ${day}`;
}

/**
 * "24 Aug", in the account's zone.
 *
 * The family's `day` spelling (`dateLabels.ts`), which is also what `shortDay` in
 * `services/waitingOn.ts` writes into the thread entries this banner sits above. Formatting an instant is not the day arithmetic INV-UTC
 * forbids in a client — but doing it in the *browser's* zone would still put "since 24 Aug" over
 * a thread saying "since 25 Aug" for anyone whose profile and laptop disagree, which is the same
 * defect wearing smaller clothes.
 */
const dayInZone = (instant: string, timezone: string): string =>
  readableInstant(instant, timezone, 'day');

/** Chased and Resolve, which are the same two presses wherever a wait is shown. */
function WaitActions({ task, compact }: { task: Task; compact?: boolean }) {
  const queryClient = useQueryClient();
  /*
    Everything is invalidated rather than the one query this row came from. A chase changes the
    rail's order, the panel's banner and the task's thread at once, and a resolve puts the task
    back into the shortlist — so the narrow invalidation would be the surface disagreeing with
    itself, which is the failure `CarriedOver` avoids the same way.
  */
  const chase = useMutation({
    mutationFn: () => logChase(task.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const resolve = useMutation({
    mutationFn: () => resolveWaitingOn(task.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  return (
    <>
      <span className="row gap wait-actions">
        {/*
          Past tense, and the primary of the two.

          It is the answer nine times out of ten — a wait usually ends by being chased again, not
          by being delivered — and it records something you have already done, which is why it is
          "Chased" rather than "Chase". Nothing is sent to the counterparty by this app, ever;
          pressing it would be a lie in the thread if it read as an imperative.
        */}
        <button
          type="button"
          disabled={chase.isPending}
          onClick={() => chase.mutate()}
          title={`Record that you chased ${task.waitingOn}`}
        >
          Chased
        </button>
        <button
          type="button"
          className="link"
          disabled={resolve.isPending}
          onClick={() => resolve.mutate()}
          title="They delivered — the ball comes back"
        >
          Resolve
        </button>
      </span>
      {/*
        The server's refusal, where the press happened. A role can change under an open page, so
        deciding not to *offer* a control is never the whole story — see `FailureNote`.
      */}
      <FailureNote
        error={chase.error ?? resolve.error}
        testId={compact ? 'wait-row-error' : 'wait-error'}
      />
    </>
  );
}

function WaitingRow({ task, onOpenTask }: { task: Task; onOpenTask: (id: string) => void }) {
  /*
    The same gate the panel uses. A wait answers to `assign`, so a reporter on a shared project
    gets the row — the fact is worth knowing — and not two buttons that answer 403.
  */
  const { can } = useProjectRole(task.projectId);

  return (
    <li className="waiting-row" data-testid="waiting-task" data-task-title={task.title}>
      <button className="link waiting-title" onClick={() => onOpenTask(task.id)}>
        {task.title}
      </button>
      {/*
        The party and the count, in that order, because the rail is scanned for *who* has gone
        quiet — the number is what it is sorted by, not what it is read by.
      */}
      <span className="subtext waiting-age">
        {task.waitingOn} · {silenceText(task)}
      </span>
      {can('assign') && <WaitActions task={task} compact />}
    </li>
  );
}

/**
 * Everything the account is waiting on, longest silent first.
 *
 * Absent rather than empty when there is nothing: an empty section is a permanent reminder that
 * a feature exists, which is the Minimalist's ruling from Phase 40 and the rule `CarriedOver` and
 * `WorkElsewhere` both already follow.
 */
export function WaitingRail({ onOpenTask }: { onOpenTask: (id: string) => void }) {
  /*
    `waiting: true` is answered by `listWaitingOn`, which narrows to this account's open waits and
    sorts them. The order is not re-derived here — see the note at the top of this file.
  */
  const waiting = useQuery({
    queryKey: ['tasks', 'waiting'],
    queryFn: () => listTasks({ waiting: true }),
  });

  const tasks = waiting.data ?? [];
  if (tasks.length === 0) return null;

  /*
    Index zero is the longest silent, because the list arrives sorted that way. Reading the last
    element instead is the mistake `CarriedOver`'s heading made and shipped: a summary line that
    contradicts the first row under it, invisible to a spec that only checks the phrase appears
    somewhere in the band.
  */
  const worst = tasks[0]!;

  return (
    <div className="waiting-rail" data-testid="waiting-rail">
      <p className="waiting-head">
        <span>
          <strong>
            Waiting on {tasks.length} {tasks.length === 1 ? 'thing' : 'things'}
          </strong>{' '}
          <span className="subtext">
            longest {silenceText(worst)}, from {worst.waitingOn}.
          </span>
        </span>
      </p>
      <ul className="waiting-list">
        {tasks.map((task) => (
          <WaitingRow key={task.id} task={task} onOpenTask={onOpenTask} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The wait, on the screen where the task is read — one sentence, not a card.
 *
 * A `.banner.is-notice`, in the neutral colour: a promise somebody else is holding is a fact
 * about the task, not a fault. `.banner` on its own is red by definition because it is what the
 * app puts API errors in, and dressing "Waiting on Acme since 24 Aug" in alarm red is the exact
 * mistake the week review's "this week has not started yet" made.
 *
 * The chase *count* and the grade on the promise were both absent from this sentence for one
 * release, and for the right reason: nothing on the payload carried either, and counting chases
 * or comparing `promisedBy` to the browser's today would have put a second definition of each on
 * the client. Both are on the row now — `chaseCount` stored beside `lastChasedAt`, `isOverdue`
 * derived from the same rule the `overdue` filter selects by — so the sentence reads them and
 * still computes nothing.
 */
export function WaitingBanner({ task }: { task: Task }) {
  const { can } = useProjectRole(task.projectId);
  /*
    The zone is asked of the account, not of the browser, and off the `['me']` cache every screen
    in the app already holds — `useProjectRole` above is reading the same one. Falling back to UTC
    while it loads rather than to the browser's zone: a wrong date that changes under the reader
    once `/api/me` answers is worse than one that is briefly the server's own.
  */
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const timezone = me.data?.timezone ?? 'UTC';
  if (!task.isWaiting || task.waitingSince === null) return null;

  return (
    <div className="wait-banner" data-testid="waiting-banner">
      <p className="banner is-notice">
        Waiting on <strong>{task.waitingOn}</strong> since {dayInZone(task.waitingSince, timezone)}
        {/*
          The promised day, graded when the grade is bad news and left plain when it is not.

          `overdue` is the server's word — the same rule the rail's filter selects by — so this is
          a reading of the payload, not a comparison. Emphasised rather than recoloured: red here
          is the error colour (see the note above), and a promise somebody has missed is still not
          a fault in the app.
        */}
        {task.promisedBy && (
          <>
            {' · '}
            {task.isOverdue ? (
              <strong>promised {readableDay(task.promisedBy)}, overdue</strong>
            ) : (
              <>promised {readableDay(task.promisedBy)}</>
            )}
          </>
        )}
        {' · '}
        {chaseText(task, timezone)}
        {' · '}
        {silenceText(task)}.
      </p>
      {can('assign') && <WaitActions task={task} />}
    </div>
  );
}

/**
 * Handing it over, at the rung that decides who acts next.
 *
 * Behind a disclosure rather than a permanent pair of fields: most tasks are nobody else's, and
 * two inputs on every panel is rent charged to every reader for a state few tasks are in.
 *
 * Offered only when nothing is being waited for. Re-setting a wait on a task that already has one
 * starts a *new* wait on the server — a deliberate ruling, since the silence you are measuring is
 * silence since this statement — and that is not a thing to be able to do by accident from a form
 * that looks like an edit. Changing the party is Resolve then set again, which is two presses and
 * says truthfully in the thread what happened.
 *
 * Not rendered at all for a role below `assign`. The panel already prints `why('assign')` once at
 * the top, and a second copy of the same sentence beside every gated control is what it was
 * written to avoid.
 */
export function WaitingOnField({ task }: { task: Task }) {
  const queryClient = useQueryClient();
  const { can } = useProjectRole(task.projectId);
  const [open, setOpen] = useState(false);
  const [party, setParty] = useState('');
  const [promisedBy, setPromisedBy] = useState('');

  const hand = useMutation({
    mutationFn: () =>
      setWaitingOn(task.id, {
        waitingOn: party.trim(),
        promisedBy: promisedBy === '' ? null : promisedBy,
      }),
    onSuccess: () => {
      setOpen(false);
      setParty('');
      setPromisedBy('');
      void queryClient.invalidateQueries();
    },
  });

  if (task.isWaiting || !can('assign')) return null;

  if (!open) {
    return (
      <p className="wait-offer">
        <button className="link" onClick={() => setOpen(true)}>
          Waiting on…
        </button>
      </p>
    );
  }

  return (
    <form
      className="wait-form"
      data-testid="waiting-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (party.trim() !== '') hand.mutate();
      }}
    >
      <label>
        Waiting on
        {/*
          A name typed by hand, not a picker. There is no contacts entity and there will not be
          one: nothing is ever sent to this person, so an address would be a field nothing reads.
        */}
        <input
          value={party}
          autoFocus
          placeholder="Acme"
          onChange={(e) => setParty(e.target.value)}
        />
      </label>
      <label>
        Promised by
        <input
          type="date"
          value={promisedBy}
          aria-label="Promised by"
          onChange={(e) => setPromisedBy(e.target.value)}
        />
      </label>
      <span className="row gap">
        <button type="submit" disabled={party.trim() === '' || hand.isPending}>
          Hand it over
        </button>
        <button type="button" className="link" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </span>
      <FailureNote error={hand.error} testId="wait-set-error" />
    </form>
  );
}
